import { useEffect, useRef, useState, type ReactNode, type PointerEvent as RPE } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useBackHandler } from '../../hooks/useBackHandler';
import { useSheetTransition, SHEET_MS } from '../../hooks/useSheetTransition';
import { CloseIcon } from '../icons';

const CLOSE_THRESHOLD = 90; // px dragged before dismissing
const ENGAGE = 6; // px before a gesture is decided as drag vs scroll

interface Props {
  onClose: () => void;
  /** Sheet content. Pass a function to receive `close` — an animated dismiss to
   *  wire to inner buttons (e.g. a Done/Confirm that should close the sheet). */
  children: ReactNode | ((close: () => void) => ReactNode);
  /** Classes for the scrollable panel (bg, radius, padding, inner layout). */
  panelClassName?: string;
  /** Show the top-right close X (default true). */
  showClose?: boolean;
  /** Overlay z-index (default 60; dialogs stack at 70). */
  z?: number;
}

/** Smooth slide-up bottom sheet — dimmed backdrop that fades, drag-to-dismiss
 *  (from anywhere while the content is at the top, always from the grab handle),
 *  a close X and native Telegram BackButton. Mirrors the reference wallet's
 *  BottomSheet: every close path animates out before unmounting. */
export function Sheet({ onClose, children, panelClassName = '', showClose = true, z = 60 }: Props) {
  const { t } = useTranslation();
  const { visible, dismiss } = useSheetTransition(onClose);
  useBackHandler(true, dismiss);

  const panelRef = useRef<HTMLDivElement>(null);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const decidedRef = useRef(false); // gesture resolved as a scroll → ignore for drag
  const fromHandleRef = useRef(false); // started on the grab handle → always drags
  const dragYRef = useRef(0);
  const startY = useRef(0);
  const downRef = useRef(false);

  // While dragging, stop the WebView from also scrolling (React's touchmove is
  // passive, so attach a non-passive listener by hand).
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const onTouchMove = (e: TouchEvent) => {
      if (draggingRef.current) e.preventDefault();
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, []);

  const onPointerDown = (e: RPE<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    downRef.current = true;
    startY.current = e.clientY;
    draggingRef.current = false;
    decidedRef.current = false;
  };
  const onPointerMove = (e: RPE<HTMLDivElement>) => {
    if (!downRef.current) return;
    const dy = e.clientY - startY.current;
    if (!draggingRef.current) {
      if (decidedRef.current) return;
      // Content scrolled down → let it scroll (unless the drag began on the handle).
      if (!fromHandleRef.current && (panelRef.current?.scrollTop ?? 0) > 0) {
        decidedRef.current = true;
        return;
      }
      if (dy > ENGAGE) {
        draggingRef.current = true;
        setDragging(true);
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      } else if (dy < -ENGAGE) {
        decidedRef.current = true;
        return;
      } else {
        return;
      }
    }
    dragYRef.current = dy > 0 ? dy : 0;
    setDragY(dragYRef.current);
  };
  const endDrag = () => {
    downRef.current = false;
    decidedRef.current = false;
    fromHandleRef.current = false;
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDragging(false);
    if (dragYRef.current > CLOSE_THRESHOLD) dismiss();
    else setDragY(0);
    dragYRef.current = 0;
  };

  const slideStyle = dragging
    ? { transform: `translateY(${dragY}px)`, transition: 'none' as const }
    : { transitionDuration: `${SHEET_MS}ms` };

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: z }}>
      {/* dim backdrop */}
      <div
        onClick={dismiss}
        className={`absolute inset-0 bg-black/[0.32] transition-opacity ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDuration: `${SHEET_MS}ms` }}
      />
      {/* sliding wrapper: grab handle + panel move together */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={`absolute inset-x-0 bottom-0 mx-auto flex max-w-[440px] flex-col items-center transition-transform ease-out ${
          visible ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={slideStyle}
      >
        {/* grab handle — dragging here always closes, even when scrolled */}
        <div
          onPointerDown={() => {
            fromHandleRef.current = true;
          }}
          className="mb-[6px] flex h-6 w-[64px] shrink-0 cursor-grab items-center justify-center"
        >
          <span className="h-[6px] w-full rounded-full bg-card/[0.67]" />
        </div>
        {/* panel */}
        <div
          ref={panelRef}
          className={`relative w-full max-h-[90vh] touch-pan-y overflow-y-auto overscroll-contain ${panelClassName}`}
        >
          {showClose && (
            <button
              onClick={dismiss}
              aria-label={t('common.close')}
              className="absolute right-4 top-4 z-30 grid size-9 place-items-center rounded-full text-ink active:scale-95"
              style={{ backgroundColor: 'rgb(var(--sheet) / 0.8)' }}
            >
              <CloseIcon width={20} height={20} />
            </button>
          )}
          {typeof children === 'function' ? children(dismiss) : children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
